import type { ReactNode, RefObject } from 'react'
import { cmsTextOrFallback } from '@/api/helpers'
import { colors } from '@/styles/colors'
import { cmsParagraph, cmsTextTitle } from '@/styles/cmsText'
import type { ServicePageContent, ServicePageFeature, ServicePageTech } from '@/api/types'

type ProblemCard = ServicePageContent['problemCards'][number]

const fade = (visible: boolean, extra = '') =>
  `transition-all duration-1000 ${extra} ${visible ? 'opacity-100' : 'opacity-0'}`

const fadeUp = (visible: boolean, extra = '') =>
  `transition-all duration-1000 transform ${extra} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`

const DESKTOP_APPROACH_STEP_LAYOUT = [
  { left: 120, top: 198 },
  { left: 992, top: 407 },
  { left: 120, top: 616 },
  { left: 992, top: 825 },
  { left: 120, top: 1034 },
]

const WORKFLOW_SECTION_INTRO =
  'We follow a user-centric and agile workflow that balances creativity with precision. Through collaboration, iteration, and continuous improvement, we ensure every project moves smoothly from concept to completion.'

const DESKTOP_WORKFLOW_LAYOUT: Array<{
  left: number
  top: number
  width: number
  height: number
  background: string
  backgroundSize?: string
  backgroundPosition?: string
  descriptionColor: string
  descriptionFont: string
}> = [
  {
    left: 120,
    top: 266,
    width: 890,
    height: 170,
    background:
      'linear-gradient(0deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(/images/64857.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    descriptionColor: '#E5E1E1',
    descriptionFont: "'Instrument Sans', sans-serif",
  },
  {
    left: 1026,
    top: 266,
    width: 774,
    height: 170,
    background: colors.primary[950],
    descriptionColor: colors.neutral[95],
    descriptionFont: "'Segoe UI', sans-serif",
  },
  {
    left: 120,
    top: 466,
    width: 774,
    height: 170,
    background: colors.primary[950],
    descriptionColor: colors.neutral[95],
    descriptionFont: "'Segoe UI', sans-serif",
  },
  {
    left: 910,
    top: 466,
    width: 890,
    height: 170,
    background:
      'linear-gradient(0deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(/images/97640.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    descriptionColor: colors.neutral[95],
    descriptionFont: "'Segoe UI', sans-serif",
  },
]

function WorkflowDesktopCard({ item, index }: { item: ServicePageFeature; index: number }) {
  const layout = DESKTOP_WORKFLOW_LAYOUT[index]
  if (!layout) return null

  return (
    <article
      style={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '24px',
        gap: '10px',
        position: 'absolute',
        width: layout.width,
        minHeight: layout.height,
        height: 'auto',
        left: layout.left,
        top: layout.top,
        background: layout.background,
        backgroundSize: layout.backgroundSize,
        backgroundPosition: layout.backgroundPosition,
        borderRadius: '24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: 0,
          gap: '24px',
          width: layout.width - 48,
          flex: 1,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
          }}
        >
          {item.icon ? (
            <img
              src={item.icon}
              alt=""
              style={{
                width: '40px',
                height: '40px',
                flexShrink: 0,
                filter: 'brightness(0) invert(1)',
                objectFit: 'contain',
              }}
            />
          ) : null}
          <h3
            title={cmsTextTitle(item.title)}
            style={{
              margin: 0,
              fontFamily: "'Instrument Sans', sans-serif",
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '29px',
              color: colors.neutral[100],
              ...cmsParagraph(),
            }}
          >
            {item.title}
          </h3>
        </div>
        <p
          title={cmsTextTitle(item.description)}
          style={{
            margin: 0,
            width: '100%',
            fontFamily: layout.descriptionFont,
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '24px',
            lineHeight: '32px',
            color: layout.descriptionColor,
            ...cmsParagraph(),
          }}
        >
          {item.description}
        </p>
      </div>
    </article>
  )
}

const DESKTOP_APPROACH_LAYOUT: Array<{
  left: number
  top: number
  background: string
  titleColor: string
  descColor: string
}> = [
  { left: 120, top: 317, background: '#3D94D0', titleColor: '#FFFFFF', descColor: '#F4F0EF' },
  { left: 992, top: 526, background: '#E2EFF8', titleColor: '#17415E', descColor: '#484646' },
  { left: 120, top: 735, background: '#E2EFF8', titleColor: '#17415E', descColor: '#484646' },
  { left: 992, top: 944, background: '#E2EFF8', titleColor: '#17415E', descColor: '#484646' },
  { left: 120, top: 1153, background: '#E2EFF8', titleColor: '#17415E', descColor: '#484646' },
]

const TECH_SECTION_INTRO =
  "We're Proud to collaborate with industry leaders and innovative businesses worldwide."

const DEFAULT_PROBLEM_CARDS_TITLE = 'If This Sounds Familiar, We Can Help'
const DEFAULT_APPROACH_TITLE = 'Our Approach: A Clear Roadmap to Results'
const DEFAULT_APPROACH_INTRO =
  "Every successful website starts with a story.\nHere's how we turn ideas into powerful digital experiences step by step."
const DEFAULT_TECHNOLOGY_TITLE = 'Technology That Powers Your Website'
const TECH_ICON_DESKTOP = 84
const TECH_ICON_MOBILE = 52

const DESKTOP_TECH_LAYOUT: Array<{ left: number; top: number }> = [
  { left: 867, top: 72 },
  { left: 1079, top: 72 },
  { left: 1291, top: 72 },
  { left: 867, top: 276 },
  { left: 1079, top: 276 },
  { left: 1291, top: 276 },
]

function TechnologyDesktopCard({ item, index }: { item: ServicePageTech; index: number }) {
  const layout = DESKTOP_TECH_LAYOUT[index]
  if (!layout) return null

  return (
    <article
      style={{
        position: 'absolute',
        width: '180px',
        minHeight: '180px',
        height: 'auto',
        left: layout.left,
        top: layout.top,
        background: colors.primary[100],
        borderRadius: '16px',
        boxSizing: 'border-box',
        padding: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          width: '100%',
          height: '100%',
        }}
      >
        {item.icon ? (
          <img
            src={item.icon}
            alt=""
            style={{
              width: `${TECH_ICON_DESKTOP}px`,
              height: `${TECH_ICON_DESKTOP}px`,
              flexShrink: 0,
              objectFit: 'contain',
            }}
          />
        ) : null}
        <h3
          title={cmsTextTitle(item.title)}
          style={{
            margin: 0,
            width: '100%',
            fontFamily: "'Instrument Sans', sans-serif",
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '24px',
            lineHeight: '29px',
            textAlign: 'center',
            color: colors.neutral[20],
            ...cmsParagraph(),
          }}
        >
          {item.title}
        </h3>
      </div>
    </article>
  )
}

export function MobileProblemCardsSection({
  items,
  visible,
  sectionRef,
  sectionTitle,
  cmsReady = true,
}: {
  items: ProblemCard[]
  visible: boolean
  sectionRef: RefObject<HTMLElement | null>
  sectionTitle?: string
  cmsReady?: boolean
}) {
  const resolvedTitle = cmsTextOrFallback(sectionTitle, DEFAULT_PROBLEM_CARDS_TITLE, cmsReady)

  return (
    <section
      ref={sectionRef as RefObject<HTMLDivElement>}
      className={fade(visible)}
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '900px',
        padding: '45px 16px 32px',
        background: '#000000',
      }}
    >
      <h2
        title={cmsTextTitle(resolvedTitle)}
        style={{
          width: '100%',
          maxWidth: '360px',
          margin: '0 auto 40px',
          position: 'relative',
          zIndex: 1,
          fontFamily: "'Instrument Sans', sans-serif",
          fontWeight: 600,
          fontSize: '28px',
          lineHeight: '37px',
          textAlign: 'center',
          color: '#FFFFFF',
          ...cmsParagraph(),
        }}
      >
        {resolvedTitle}
      </h2>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '360px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {items.map((item, index) => (
          <div key={item.id ?? index}>
            <div
              className={fade(visible)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '24px 0',
                gap: '12px',
                transitionDelay: `${index * 100}ms`,
              }}
            >
              <h3
                title={cmsTextTitle(item.title)}
                style={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontWeight: 500,
                  fontSize: '22px',
                  lineHeight: '28px',
                  color: '#FFFFFF',
                  margin: 0,
                  ...cmsParagraph(),
                }}
              >
                {item.title}
              </h3>
              <p
                title={cmsTextTitle(item.description)}
                style={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: 'rgba(255, 255, 255, 0.65)',
                  margin: 0,
                  ...cmsParagraph(),
                }}
              >
                {item.description}
              </p>
            </div>
            {index < items.length - 1 ? (
              <div style={{ width: '100%', height: '1px', background: 'rgba(255, 255, 255, 0.25)' }} />
            ) : null}
          </div>
        ))}
      </div>
    </section>
  )
}

export function DesktopProblemCardsSection({
  items,
  visible,
  sectionRef,
  top,
  sectionTitle,
  cmsReady = true,
}: {
  items: ProblemCard[]
  visible: boolean
  sectionRef: RefObject<HTMLElement | null>
  top: number
  sectionTitle?: string
  cmsReady?: boolean
}) {
  const resolvedTitle = cmsTextOrFallback(sectionTitle, DEFAULT_PROBLEM_CARDS_TITLE, cmsReady)

  return (
    <section
      ref={sectionRef as RefObject<HTMLDivElement>}
      className={fadeUp(visible)}
      style={{
        position: 'absolute',
        width: '1920px',
        height: '1402px',
        left: 'calc(50% - 1920px/2)',
        top: `${top}px`,
        background: '#000000',
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: '1917px',
          height: '1575px',
          left: '3px',
          top: '-375px',
          background: 'linear-gradient(180deg, rgba(14, 29, 39, 0) 0%, #07141D 21.59%, #000000 45%)',
          pointerEvents: 'none',
        }}
      />
      <h2
        title={cmsTextTitle(resolvedTitle)}
        style={{
          position: 'absolute',
          width: '1200px',
          left: 'calc(50% - 600px)',
          top: '58px',
          fontFamily: "'Instrument Sans', sans-serif",
          fontWeight: 600,
          fontSize: '48px',
          lineHeight: '59px',
          textAlign: 'center',
          color: '#FFFFFF',
          margin: 0,
          ...cmsParagraph(),
        }}
      >
        {resolvedTitle}
      </h2>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          width: '1682px',
          left: '120px',
          top: '172px',
        }}
      >
        {items.map((item, index) => (
          <div key={item.id ?? index}>
            <div
              className={fade(visible)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '102px 32px',
                width: '1682px',
                minHeight: '276px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'row', gap: '24px', width: '100%' }}>
                <h3
                  title={cmsTextTitle(item.title)}
                  style={{
                    width: '822px',
                    fontFamily: "'Instrument Sans', sans-serif",
                    fontWeight: 400,
                    fontSize: '44px',
                    lineHeight: '54px',
                    color: '#FFFFFF',
                    margin: 0,
                    ...cmsParagraph(),
                  }}
                >
                  {item.title}
                </h3>
                <p
                  title={cmsTextTitle(item.description)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: "'Instrument Sans', sans-serif",
                    fontWeight: 400,
                    fontSize: '28px',
                    lineHeight: '34px',
                    color: 'rgba(255, 255, 255, 0.52)',
                    margin: 0,
                    ...cmsParagraph(),
                  }}
                >
                  {item.description}
                </p>
              </div>
            </div>
            {index < items.length - 1 ? (
              <div style={{ width: '1680px', height: '0', border: '1px solid rgba(255, 255, 255, 0.44)' }} />
            ) : null}
          </div>
        ))}
      </div>
    </section>
  )
}

const MOBILE_WORKFLOW_BACKGROUNDS = [
  'linear-gradient(0deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(/images/64857.jpg)',
  null,
  null,
  'linear-gradient(0deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(/images/97640.jpg)',
] as const

export function MobileWorkflowSection({
  items,
  sectionRef,
  intro = WORKFLOW_SECTION_INTRO,
}: {
  items: ServicePageFeature[]
  sectionRef: RefObject<HTMLElement | null>
  intro?: string
}) {
  return (
    <section
      ref={sectionRef as RefObject<HTMLDivElement>}
      style={{
        position: 'relative',
        width: '100%',
        padding: '32px 16px',
        background: colors.background.white,
      }}
    >
      <h2
        style={{
          fontFamily: "'Instrument Sans', sans-serif",
          fontWeight: 700,
          fontSize: '28px',
          lineHeight: '37px',
          color: colors.neutral[6],
          margin: '0 0 16px',
        }}
      >
        Our <span style={{ color: colors.primary[500] }}>Workflow</span>
      </h2>
      <p
        title={cmsTextTitle(intro)}
        style={{
          margin: '0 0 24px',
          fontFamily: "'Instrument Sans', sans-serif",
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '21px',
          color: colors.neutral[30],
          ...cmsParagraph(),
        }}
      >
        {intro}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '360px', margin: '0 auto' }}>
        {items.map((item, index) => {
          const bgImage = MOBILE_WORKFLOW_BACKGROUNDS[index]
          return (
          <div
            key={item.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '16px',
              gap: '16px',
              background: bgImage ?? colors.primary[950],
              backgroundSize: bgImage ? 'cover' : undefined,
              backgroundPosition: bgImage ? 'center' : undefined,
              borderRadius: '16px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
              {item.icon ? (
                <img
                  src={item.icon}
                  alt=""
                  style={{ width: '24px', height: '24px', filter: 'brightness(0) invert(1)' }}
                />
              ) : null}
              <h3
                title={cmsTextTitle(item.title)}
                style={{
                  fontFamily: "'Segoe UI', sans-serif",
                  fontWeight: 600,
                  fontSize: '16px',
                  color: colors.neutral[100],
                  margin: 0,
                  flex: 1,
                  minWidth: 0,
                  ...cmsParagraph(),
                }}
              >
                {item.title}
              </h3>
            </div>
            <p
              title={cmsTextTitle(item.description)}
              style={{
                fontFamily: "'Segoe UI', sans-serif",
                fontSize: '14px',
                color: colors.neutral[90],
                margin: 0,
                ...cmsParagraph(),
              }}
            >
              {item.description}
            </p>
          </div>
          )
        })}
      </div>
    </section>
  )
}

export function DesktopWorkflowSection({
  items,
  sectionRef,
  top,
  intro = WORKFLOW_SECTION_INTRO,
}: {
  items: ServicePageFeature[]
  sectionRef: RefObject<HTMLElement | null>
  top: number
  intro?: string
}) {
  return (
    <section
      ref={sectionRef as RefObject<HTMLDivElement>}
      style={{
        position: 'absolute',
        width: '1920px',
        height: '714px',
        left: 'calc(50% - 1920px/2)',
        top: `${top}px`,
        background: colors.background.white,
      }}
    >
      <h2
        style={{
          position: 'absolute',
          width: '319px',
          left: '120px',
          top: '72px',
          margin: 0,
          fontFamily: "'Instrument Sans', sans-serif",
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '48px',
          lineHeight: '59px',
          color: colors.neutral[6],
          ...cmsParagraph(),
        }}
      >
        Our <span style={{ color: colors.primary[500] }}>Workflow</span>
      </h2>
      <p
        title={cmsTextTitle(intro)}
        style={{
          position: 'absolute',
          width: '1396px',
          left: '120px',
          top: '152px',
          margin: 0,
          fontFamily: "'Instrument Sans', sans-serif",
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '28px',
          lineHeight: '34px',
          color: colors.neutral[30],
          ...cmsParagraph(),
        }}
      >
        {intro}
      </p>
      {items.map((item, index) => (
        <WorkflowDesktopCard key={item.id ?? index} item={item} index={index} />
      ))}
    </section>
  )
}

export function MobileApproachSection({
  items,
  visible,
  sectionRef,
  sectionTitle,
  sectionIntro,
  cmsReady = true,
}: {
  items: ServicePageFeature[]
  visible: boolean
  sectionRef: RefObject<HTMLElement | null>
  sectionTitle?: string
  sectionIntro?: string
  cmsReady?: boolean
}) {
  const resolvedTitle = cmsTextOrFallback(sectionTitle, DEFAULT_APPROACH_TITLE, cmsReady)
  const resolvedIntro = cmsTextOrFallback(sectionIntro, DEFAULT_APPROACH_INTRO, cmsReady)

  return (
    <section
      ref={sectionRef as RefObject<HTMLDivElement>}
      className={fade(visible)}
      style={{
        position: 'relative',
        width: '100%',
        padding: '32px 16px',
        background: colors.background.white,
      }}
    >
      <h2
        title={cmsTextTitle(resolvedTitle)}
        style={{
          fontFamily: "'Segoe UI', sans-serif",
          fontWeight: 700,
          fontSize: '28px',
          color: colors.neutral[6],
          margin: '0 0 12px',
          ...cmsParagraph(),
        }}
      >
        {resolvedTitle}
      </h2>
      <p
        title={cmsTextTitle(resolvedIntro)}
        style={{
          fontFamily: "'Segoe UI', sans-serif",
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          color: colors.neutral[30],
          margin: '0 0 24px',
          ...cmsParagraph(),
        }}
      >
        {resolvedIntro}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '360px', margin: '0 auto' }}>
        {items.map((item, index) => (
          <div
            key={item.id ?? index}
            className={fade(visible)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '16px',
              gap: '10px',
              background: index === 0 ? colors.primary[500] : colors.primary[100],
              borderRadius: '16px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
              {item.icon ? <img src={item.icon} alt="" style={{ width: '24px', height: '24px' }} /> : null}
              <h3
                title={cmsTextTitle(item.title)}
                style={{
                  fontFamily: "'Segoe UI', sans-serif",
                  fontWeight: 600,
                  fontSize: '16px',
                  color: index === 0 ? colors.neutral[100] : colors.primary[800],
                  margin: 0,
                  flex: 1,
                  minWidth: 0,
                  ...cmsParagraph(),
                }}
              >
                {item.title}
              </h3>
            </div>
            <p
              title={cmsTextTitle(item.description)}
              style={{
                fontFamily: "'Segoe UI', sans-serif",
                fontSize: '14px',
                color: index === 0 ? colors.neutral[95] : colors.neutral[30],
                margin: 0,
                ...cmsParagraph(),
              }}
            >
              {item.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

export function ApproachDecorativeArrows() {
  return (
    <>
      <div style={{ position: 'absolute', width: '468px', height: '121px', left: '524px', top: '493px', pointerEvents: 'none' }}>
        <svg width="468" height="121" viewBox="0 0 468 121" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible' }}>
          <path d="M 0 10 Q 150 111 448 111" stroke="#3D94D0" strokeWidth="3" strokeDasharray="6 6" strokeLinecap="round" fill="none" />
          <path d="M 440 104 L 448 111 L 440 118" stroke="#3D94D0" strokeWidth="3" strokeLinecap="round" fill="none" />
        </svg>
      </div>
      <div style={{ position: 'absolute', width: '468px', height: '121px', left: '928px', top: '702px', pointerEvents: 'none' }}>
        <svg width="468" height="121" viewBox="0 0 468 121" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible' }}>
          <path d="M 468 10 Q 318 111 20 111" stroke="#3D94D0" strokeWidth="3" strokeDasharray="6 6" strokeLinecap="round" fill="none" />
          <path d="M 28 104 L 20 111 L 28 118" stroke="#3D94D0" strokeWidth="3" strokeLinecap="round" fill="none" />
        </svg>
      </div>
      <div style={{ position: 'absolute', width: '468px', height: '121px', left: '524px', top: '911px', pointerEvents: 'none' }}>
        <svg width="468" height="121" viewBox="0 0 468 121" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible' }}>
          <path d="M 0 10 Q 150 111 448 111" stroke="#3D94D0" strokeWidth="3" strokeDasharray="6 6" strokeLinecap="round" fill="none" />
          <path d="M 440 104 L 448 111 L 440 118" stroke="#3D94D0" strokeWidth="3" strokeLinecap="round" fill="none" />
        </svg>
      </div>
      <div style={{ position: 'absolute', width: '468px', height: '121px', left: '928px', top: '1120px', pointerEvents: 'none' }}>
        <svg width="468" height="121" viewBox="0 0 468 121" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible' }}>
          <path d="M 468 10 Q 318 111 20 111" stroke="#3D94D0" strokeWidth="3" strokeDasharray="6 6" strokeLinecap="round" fill="none" />
          <path d="M 28 104 L 20 111 L 28 118" stroke="#3D94D0" strokeWidth="3" strokeLinecap="round" fill="none" />
        </svg>
      </div>
    </>
  )
}

export function DesktopApproachSection({
  items,
  visible,
  sectionRef,
  top,
  children,
  sectionTitle,
  sectionIntro,
  cmsReady = true,
}: {
  items: ServicePageFeature[]
  visible: boolean
  sectionRef: RefObject<HTMLElement | null>
  top: number
  children?: ReactNode
  sectionTitle?: string
  sectionIntro?: string
  cmsReady?: boolean
}) {
  const resolvedTitle = cmsTextOrFallback(sectionTitle, DEFAULT_APPROACH_TITLE, cmsReady)
  const resolvedIntro = cmsTextOrFallback(sectionIntro, DEFAULT_APPROACH_INTRO, cmsReady)

  return (
    <section
      ref={sectionRef as RefObject<HTMLDivElement>}
      className={fadeUp(visible)}
      style={{
        position: 'absolute',
        width: '1920px',
        height: '1401px',
        left: 'calc(50% - 1920px/2)',
        top: `${top}px`,
        background: '#FFFFFF',
      }}
    >
      <h2
        title={cmsTextTitle(resolvedTitle)}
        style={{
          position: 'absolute',
          left: '120px',
          top: '60px',
          width: '1396px',
          fontFamily: "'Instrument Sans', sans-serif",
          fontWeight: 700,
          fontSize: '48px',
          color: '#141218',
          margin: 0,
          ...cmsParagraph(),
        }}
      >
        {resolvedTitle}
      </h2>
      <p
        title={cmsTextTitle(resolvedIntro)}
        style={{
          position: 'absolute',
          width: '1396px',
          left: '120px',
          top: '144px',
          margin: 0,
          fontFamily: "'Instrument Sans', sans-serif",
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '28px',
          lineHeight: '34px',
          color: colors.neutral[30],
          zIndex: 1,
          ...cmsParagraph(),
        }}
      >
        {resolvedIntro}
      </p>
      {items.map((item, index) => {
        const layout = DESKTOP_APPROACH_LAYOUT[index]
        const stepLayout = DESKTOP_APPROACH_STEP_LAYOUT[index]
        if (!layout) return null
        const step = String(index + 1).padStart(2, '0')
        return (
          <div key={item.id}>
            {stepLayout ? (
              <div
                style={{
                  position: 'absolute',
                  left: stepLayout.left,
                  top: stepLayout.top,
                  fontFamily: "'Segoe UI', sans-serif",
                  fontWeight: 700,
                  fontSize: '128px',
                  lineHeight: '170px',
                  color: 'rgba(61, 148, 208, 0.2)',
                }}
              >
                {step}
              </div>
            ) : null}
            <div
              className={fadeUp(visible)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '24px',
                gap: '10px',
                position: 'absolute',
                width: '808px',
                minHeight: '176px',
                left: layout.left,
                top: layout.top,
                background: layout.background,
                borderRadius: '24px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
                {item.icon ? (
                  <img src={item.icon} alt="" style={{ width: '40px', height: '40px' }} />
                ) : null}
                <h3
                  title={cmsTextTitle(item.title)}
                  style={{
                    fontFamily: "'Instrument Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: '28px',
                    color: layout.titleColor,
                    margin: 0,
                    flex: 1,
                    minWidth: 0,
                    ...cmsParagraph(),
                  }}
                >
                  {item.title}
                </h3>
              </div>
              <p
                title={cmsTextTitle(item.description)}
                style={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontSize: '24px',
                  color: layout.descColor,
                  margin: 0,
                  ...cmsParagraph(),
                }}
              >
                {item.description}
              </p>
            </div>
          </div>
        )
      })}
      {children}
    </section>
  )
}

export function MobileTechnologySection({
  items,
  sectionRef,
  sectionTitle,
  intro,
  cmsReady = true,
}: {
  items: ServicePageTech[]
  sectionRef: RefObject<HTMLElement | null>
  sectionTitle?: string
  intro?: string
  cmsReady?: boolean
}) {
  const resolvedTitle = cmsTextOrFallback(sectionTitle, DEFAULT_TECHNOLOGY_TITLE, cmsReady)
  const resolvedIntro = cmsTextOrFallback(intro, TECH_SECTION_INTRO, cmsReady)

  return (
    <section
      ref={sectionRef as RefObject<HTMLDivElement>}
      style={{
        position: 'relative',
        width: '100%',
        padding: '32px 16px',
        background: colors.background.white,
      }}
    >
      <div
        style={{
          maxWidth: '360px',
          margin: '0 auto',
          padding: '24px',
          background: colors.primary[50],
          borderRadius: '16px',
        }}
      >
        <h2
          title={cmsTextTitle(resolvedTitle)}
          style={{
            fontFamily: "'Instrument Sans', sans-serif",
            fontWeight: 700,
            fontSize: '28px',
            lineHeight: '37px',
            color: colors.neutral[6],
            margin: '0 0 16px',
            ...cmsParagraph(),
          }}
        >
          {resolvedTitle}
        </h2>
        <p
          title={cmsTextTitle(resolvedIntro)}
          style={{
            margin: '0 0 24px',
            fontFamily: "'Instrument Sans', sans-serif",
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '21px',
            color: colors.neutral[30],
            ...cmsParagraph(),
          }}
        >
          {resolvedIntro}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {items.map((item, index) => (
            <div
              key={item.id ?? index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px',
                gap: '16px',
                background: colors.primary[100],
                borderRadius: '12px',
                minHeight: '132px',
              }}
            >
              {item.icon ? (
                <img src={item.icon} alt={item.title} style={{ width: `${TECH_ICON_MOBILE}px`, height: `${TECH_ICON_MOBILE}px`, objectFit: 'contain' }} />
              ) : null}
              <div
                title={cmsTextTitle(item.title)}
                style={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '21px',
                  textAlign: 'center',
                  color: colors.neutral[20],
                  width: '100%',
                  ...cmsParagraph(),
                }}
              >
                {item.title}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
export function DesktopTechnologySection({
  items,
  sectionRef,
  top,
  sectionTitle,
  intro,
  cmsReady = true,
}: {
  items: ServicePageTech[]
  sectionRef: RefObject<HTMLElement | null>
  top: number
  sectionTitle?: string
  intro?: string
  cmsReady?: boolean
}) {
  const resolvedTitle = cmsTextOrFallback(sectionTitle, DEFAULT_TECHNOLOGY_TITLE, cmsReady)
  const resolvedIntro = cmsTextOrFallback(intro, TECH_SECTION_INTRO, cmsReady)

  return (
    <section
      ref={sectionRef as RefObject<HTMLDivElement>}
      style={{
        position: 'absolute',
        width: '1680px',
        height: '528px',
        left: 'calc(50% - 1680px/2)',
        top: `${top}px`,
        background: colors.primary[50],
        borderRadius: '32px',
      }}
    >
      <h2
        title={cmsTextTitle(resolvedTitle)}
        style={{
          position: 'absolute',
          width: '611px',
          left: '56px',
          top: '72px',
          margin: 0,
          fontFamily: "'Instrument Sans', sans-serif",
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '48px',
          lineHeight: '59px',
          color: colors.neutral[6],
          ...cmsParagraph(),
        }}
      >
        {resolvedTitle}
      </h2>
      <p
        title={cmsTextTitle(resolvedIntro)}
        style={{
          position: 'absolute',
          width: '611px',
          left: '56px',
          top: '240px',
          margin: 0,
          fontFamily: "'Instrument Sans', sans-serif",
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '28px',
          lineHeight: '34px',
          color: colors.neutral[30],
          ...cmsParagraph(),
        }}
      >
        {resolvedIntro}
      </p>
      {items.map((item, index) => (
        <TechnologyDesktopCard key={item.id ?? index} item={item} index={index} />
      ))}
    </section>
  )
}

